import { t } from '@lingui/core/macro';
import { BookingLocationType, BookingProposalRecordList, BookingRecordList, BookingStatus } from '@navio/nsdk';
import { CalendarEntryStatus, CalendarStatusLegendItem } from 'noddi-ui-web';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';

export type CalendarStatusKey = 'upcoming' | 'completed';

export type FleetCalendarBooking = BookingRecordList;

export type FleetCalendarProposal = BookingProposalRecordList;

/** Legend and day-cell order. */
export const CALENDAR_STATUS_KEYS: CalendarStatusKey[] = ['upcoming', 'completed'];

/** Onto the design system's shared calendar vocabulary — a customer's "upcoming" is "planned". */
export const CALENDAR_ENTRY_STATUS: Record<CalendarStatusKey, CalendarEntryStatus> = {
  upcoming: 'planned',
  completed: 'completed'
};

export const getCalendarStatusLegendItems = (): CalendarStatusLegendItem[] =>
  CALENDAR_STATUS_KEYS.map((status) => ({
    status: CALENDAR_ENTRY_STATUS[status],
    label: getCalendarStatusLabel(status)
  }));

export const getCalendarStatusKey = (status: BookingStatus): CalendarStatusKey | null => {
  switch (status) {
    case BookingStatus.CONFIRMED:
    case BookingStatus.ASSIGNED_TO_ROUTE:
      return 'upcoming';
    case BookingStatus.COMPLETED:
      return 'completed';
    default:
      return null;
  }
};

export const getCalendarStatusLabel = (status: CalendarStatusKey): string => {
  switch (status) {
    case 'upcoming':
      return t`Upcoming`;
    case 'completed':
      return t`Completed`;
  }
};

/** Stationary bookings are an appointment at a time, not a delivery window: show the exact start. */
export const formatBookingTime = (booking: FleetCalendarBooking): string =>
  booking.locationType === BookingLocationType.STATIONARY
    ? formatTimeOfDay(booking.deliveryWindowStartsAt)
    : formatTimeWindow(booking.deliveryWindowStartsAt, booking.deliveryWindowEndsAt);

const formatTimeWindow = (startsAt: string, endsAt: string): string => {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const startHour = start.getHours().toString().padStart(2, '0');
  const endHour = end.getHours().toString().padStart(2, '0');
  return `${startHour}–${endHour}`;
};

export const formatTimeOfDay = (startsAt: string): string => noddiFormatDate(startsAt, NoddiDateFormats.TIME);

export const getDateKey = (deliveryWindowStartsAt: string): string => deliveryWindowStartsAt.split('T')[0]!;
